import { z } from "zod";

import { shippingAddressSchema } from "./checkout.js";
import { designDocumentSchema } from "./designs.js";
import { certificateOptionSchema, packagingOptionSchema } from "./pricing.js";
import {
  isoDateTimeSchema,
  millimeterSchema,
  moneyFenSchema,
  positiveIntSchema,
  uuidSchema,
} from "./primitives.js";
import { shippingReasonSchema } from "./shipping.js";

/**
 * 订单。
 *
 * 订单是**一份冻结的快照**：设计、材料价格、包装与证书服务费、运费与当时的运费规则版本
 * 全部在下单那一刻定死。之后后台改价、改运费规则、下架商品，都不会改动已下的订单
 * ——否则「用户看到的价」与「实际收的钱」会变成两笔账。
 *
 * 库存采用**预占**而不是立即扣减：下单只锁定数量，确认收款时才真正从库存里扣。
 * 预占有期限，超时未确认收款就自动失效，别人可以重新买（见 `ORDER_PAYMENT_WINDOW_MINUTES`）。
 * 因此库存表上的数字始终是「实物还有多少」，可售数量由服务端按未失效的预占算出来。
 */

/** 下单后给用户的付款时限。超时未确认收款，预占自动失效。 */
export const ORDER_PAYMENT_WINDOW_MINUTES = 30;

export const orderStatusSchema = z.enum(["pending_payment", "paid", "cancelled", "expired"]);
export type OrderStatus = z.infer<typeof orderStatusSchema>;

/** 制作和配送独立于收款状态。 */
export const fulfillmentStatusSchema = z.enum(["awaiting", "crafting", "completed", "shipped"]);
export type FulfillmentStatus = z.infer<typeof fulfillmentStatusSchema>;

export const proofStatusSchema = z.enum([
  "none",
  "awaiting_confirmation",
  "adjustment_requested",
  "confirmed",
]);
export type ProofStatus = z.infer<typeof proofStatusSchema>;
export const orderProofSchema = z.object({
  revision: z.int().positive(),
  photoPaths: z
    .array(z.string().regex(/^\/media\/[a-f0-9]{2}\/[a-f0-9]{64}\.(jpg|png|webp)$/))
    .min(1)
    .max(6),
  status: proofStatusSchema.exclude(["none"]),
  feedback: z.string().min(2).max(200).nullable(),
  submittedAt: isoDateTimeSchema,
  decidedAt: isoDateTimeSchema.nullable(),
});
export type OrderProofPayload = z.infer<typeof orderProofSchema>;
export const submitOrderProofSchema = z.strictObject({
  expectedRevision: z.int().nonnegative(),
  photoPaths: orderProofSchema.shape.photoPaths,
});
export type SubmitOrderProofPayload = z.infer<typeof submitOrderProofSchema>;
export const decideOrderProofSchema = z.discriminatedUnion("decision", [
  z.strictObject({ revision: z.int().positive(), decision: z.literal("confirm") }),
  z.strictObject({
    revision: z.int().positive(),
    decision: z.literal("request_adjustment"),
    reason: z.string().trim().min(2).max(200),
  }),
]);
export type DecideOrderProofPayload = z.infer<typeof decideOrderProofSchema>;

export const updateFulfillmentRequestSchema = z.discriminatedUnion("status", [
  z.strictObject({ status: z.literal("crafting") }),
  z.strictObject({ status: z.literal("completed") }),
  z.strictObject({
    status: z.literal("shipped"),
    carrier: z.string().trim().min(1).max(64),
    trackingNo: z.string().trim().min(1).max(64),
  }),
]);
export type UpdateFulfillmentRequestPayload = z.infer<typeof updateFulfillmentRequestSchema>;

export const refundStatusSchema = z.enum([
  "requested",
  "rejected",
  "processing",
  "refunded",
  "closed",
  "abnormal",
]);
export type RefundStatus = z.infer<typeof refundStatusSchema>;
export const refundSchema = z.object({
  id: uuidSchema,
  status: refundStatusSchema,
  reason: z.string().min(1).max(200),
  reviewReason: z.string().max(200).nullable(),
  requestedAt: isoDateTimeSchema,
  reviewedAt: isoDateTimeSchema.nullable(),
  refundedAt: isoDateTimeSchema.nullable(),
  amountFen: moneyFenSchema.positive(),
});
export type RefundPayload = z.infer<typeof refundSchema>;
export const requestRefundSchema = z.strictObject({
  requestId: uuidSchema,
  reason: z.string().trim().min(2).max(200),
});
export type RequestRefundPayload = z.infer<typeof requestRefundSchema>;
export const reviewRefundSchema = z.discriminatedUnion("decision", [
  z.strictObject({ decision: z.literal("approve") }),
  z.strictObject({ decision: z.literal("reject"), reason: z.string().trim().min(2).max(200) }),
]);
export type ReviewRefundPayload = z.infer<typeof reviewRefundSchema>;

/**
 * 订单里的一行材料。**存的是下单时的副本，不是对目录的引用**：
 * 石种被删掉之后，这张订单仍然要能看清当时买的是什么。
 */
export const orderItemSchema = z.object({
  skuId: uuidSchema,
  code: z.string().min(1).max(64),
  name: z.string().min(1).max(64),
  diameterMm: millimeterSchema.positive(),
  /** 下单时的照片地址。素材地址不可覆盖，因此历史订单引用的旧图永远有效。 */
  photoPath: z.string().min(1).max(256).nullable(),
  quantity: positiveIntSchema,
  unitPriceFen: moneyFenSchema.nonnegative(),
  subtotalFen: moneyFenSchema.nonnegative(),
});

export type OrderItemPayload = z.infer<typeof orderItemSchema>;

/** 订单摘要。小程序「我的订单」与后台列表共用。 */
export const orderSummarySchema = z.object({
  id: uuidSchema,
  orderNo: z.string().min(1).max(24),
  status: orderStatusSchema,
  createdAt: isoDateTimeSchema,
  /** 预占失效时刻。已付款或已取消的订单上它只是当时约定的时限。 */
  expiresAt: isoDateTimeSchema,
  paidAt: isoDateTimeSchema.nullable(),
  cancelledAt: isoDateTimeSchema.nullable(),
  fulfillmentStatus: fulfillmentStatusSchema,
  proofStatus: proofStatusSchema,
  proofRevision: z.int().nonnegative(),
  latestRefund: refundSchema.nullable(),
  itemCount: z.int().nonnegative(),
  totalFen: moneyFenSchema.nonnegative(),
});

export type OrderSummaryPayload = z.infer<typeof orderSummarySchema>;

/**
 * 一张完整订单。小程序下单成功后、后台打开订单详情时都用它。
 *
 * 金额字段全部是**下单时冻结的值**，不是按当前目录重算的结果。
 */
export const orderSchema = orderSummarySchema.extend({
  latestProof: orderProofSchema.nullable(),
  carrier: z.string().min(1).max(64).nullable(),
  trackingNo: z.string().min(1).max(64).nullable(),
  shippedAt: isoDateTimeSchema.nullable(),
  design: designDocumentSchema,
  /** 一张订单中的全部设计；旧的单设计订单返回长度为 1 的数组。 */
  designs: z.array(designDocumentSchema).min(1).max(20),
  items: z.array(orderItemSchema).min(1),
  materialFen: moneyFenSchema.nonnegative(),
  packaging: packagingOptionSchema,
  packagingFen: moneyFenSchema.nonnegative(),
  certificate: certificateOptionSchema,
  certificateFen: moneyFenSchema.nonnegative(),
  shippingFen: moneyFenSchema.nonnegative(),
  /** 下单时生效的运费规则版本；规则未启用时为 null。 */
  shippingPolicyRevision: z.int().positive().nullable(),
  /** 下单时运费的判定依据，便于对账时解释「为什么是 8 元」。 */
  shippingReason: shippingReasonSchema,
  address: shippingAddressSchema,
  catalogRevision: positiveIntSchema,
});

export type OrderPayload = z.infer<typeof orderSchema>;

/**
 * 下单请求。
 *
 * `requestId` 是**幂等键**：小程序一次提交只生成一个，网络重试或用户连点两次都只会得到同一张订单。
 *
 * 客户端不提交材料单价，但**要提交它给用户看过的合计**（`expectedTotalFen`）：
 * 服务端按下单那一刻的目录重算，两者不一致就拒绝，而不是按新价把单建出来。
 * 用户没同意过的账单，不该悄悄生成。
 */
export const createOrderRequestSchema = z.strictObject({
  requestId: uuidSchema,
  design: designDocumentSchema,
  packaging: packagingOptionSchema.default("standard"),
  certificate: certificateOptionSchema.default("none"),
  address: shippingAddressSchema,
  expectedTotalFen: moneyFenSchema.nonnegative(),
});

export type CreateOrderRequestPayload = z.infer<typeof createOrderRequestSchema>;

export const createBatchOrderRequestSchema = createOrderRequestSchema
  .omit({ design: true })
  .extend({
    designs: z.array(designDocumentSchema).min(1).max(20),
  });
export type CreateBatchOrderRequestPayload = z.infer<typeof createBatchOrderRequestSchema>;

export const customerOrderListResponseSchema = z.object({
  items: z.array(orderSummarySchema),
});

export type CustomerOrderListResponsePayload = z.infer<typeof customerOrderListResponseSchema>;

/** 后台列表：比小程序多带收货信息，店主据此发货。 */
export const adminOrderSummarySchema = orderSummarySchema.extend({
  address: shippingAddressSchema,
  customerName: z.string().min(1).max(64),
});

export type AdminOrderSummaryPayload = z.infer<typeof adminOrderSummarySchema>;

/**
 * 后台订单列表的筛选条件。
 *
 * 与其它列表接口一样用 `z.coerce.number()`：查询串里的值都是字符串，
 * 不转换的话 `limit=200` 会被判成不合法。也不加 strict——查询串常带无关参数。
 */
export const adminOrderListQuerySchema = z.object({
  /** 不传表示全部。`expired` 会同时匹配「已标记超时」与「已过时限但还没被标记」的单。 */
  status: orderStatusSchema.optional(),
  keyword: z.string().trim().min(1).max(64).optional(),
  limit: z.coerce.number().int().min(1).max(200).optional(),
  offset: z.coerce.number().int().min(0).optional(),
});

export type AdminOrderListQueryPayload = z.infer<typeof adminOrderListQuerySchema>;

export const adminOrderListResponseSchema = z.object({
  items: z.array(adminOrderSummarySchema),
  total: z.int().nonnegative(),
});

export type AdminOrderListResponsePayload = z.infer<typeof adminOrderListResponseSchema>;
